// Weekoverzicht (vr t/m do) als print-HTML op één A3: gebruikt door "Weekoverzicht printen"
// (naslag) én de vrijdagse roostermail (pdf-bijlage via /api/weekpdf).
const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * @param {object} appData
 * @param {Date} weekBase  eerste dag (vrijdag)
 * @param {{basis: string, seed?: object}} opt  basis = origin voor de lettertypen; seed = SEED_PROFILE_PHOTOS
 * @returns {{html: string, weekLabel: string}}
 */
export const weekPrintHtml = (appData, weekBase, { basis, seed = {} } = {}) => {
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekBase);
    d.setDate(weekBase.getDate() + i);
    return { date: d, dateKey: toIso(d) };
  });
  const fmtShort = (d) => d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
  const weekLabel = `Week ${fmtShort(weekDays[0].date)} – ${fmtShort(weekDays[6].date)}`;
  const SEED_PROFILE_PHOTOS = seed;
    // ── kleurhelpers ──────────────────────────────────────────────
    const actCol = (act) => {
      const t = (act.type || "").toLowerCase(),
        ti = (act.title || "").toLowerCase();
      const crea = t === "crea";
      const sk = t === "springkussen" || ti.includes("springkussen");
      if (crea) return ["#9DB4F6", "#00192F", "#7A9FE8"]; // Sky blauw / Lagoon
      if (sk) return ["#FF699B", "#700606", "#F04080"]; // Blossom roze / Sunset
      return ["#FFCA55", "#7A5226", "#F0B030"]; // Honey geel / Bark (alles overig)
    };
    const chCol = (n, k = "bg") => {
      const r = (n || "").toLowerCase();
      if (r.includes("orry")) return k === "bg" ? "#FF699B" : "#700606";
      if (r.includes("bing")) return k === "bg" ? "#B1EDA6" : "#045E51";
      if (r.includes("woops")) return k === "bg" ? "#9DB4F6" : "#00192F";
      if (r.includes("rep")) return k === "bg" ? "#FFCA55" : "#7A5226";
      return k === "bg" ? "#e2e8f0" : "#475569";
    };
    const DS = {
      5: "VR",
      6: "ZA",
      0: "ZO",
      1: "MA",
      2: "DI",
      3: "WO",
      4: "DO",
    };
    const DF = {
      5: "Vrijdag",
      6: "Zaterdag",
      0: "Zondag",
      1: "Maandag",
      2: "Dinsdag",
      3: "Woensdag",
      4: "Donderdag",
    };

    // ── data per dag ──────────────────────────────────────────────
    const days = weekDays.map(({ date, dateKey }) => {
      const dow = date.getDay();
      const shifts = (appData.workSchedule?.[dateKey] || []).filter(
        (s) => s.matchedUser
      );
      const skData = appData.dailySpringkussen?.[dateKey];
      const sk = skData
        ? {
            open: !!skData.open,
            start: skData.start || "10:00",
            end: skData.end || "17:00",
            staff: Array.isArray(skData.staff)
              ? skData.staff.filter(Boolean)
              : [],
          }
        : { open: false, start: "10:00", end: "17:00", staff: [] };
      const acts = [
        ...(appData.schedule || []).filter((a) => a.date === dateKey),
      ].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
      const chars = (appData.characters || []).filter(
        (c) => c.date === dateKey
      );
      const byP = {};
      shifts.forEach((s) => {
        if (!byP[s.matchedUser]) byP[s.matchedUser] = [];
        byP[s.matchedUser].push(s);
      });
      const persons = Object.entries(byP)
        .map(([name, arr]) => {
          const ch = chars.find((c) => c.actor === name);
          const sorted = arr.sort((a, b) =>
            (a.start || "").localeCompare(b.start || "")
          );
          return { name, shifts: sorted, char: ch?.role || null };
        })
        .sort((a, b) =>
          (byP[a.name][0]?.start || "").localeCompare(
            byP[b.name][0]?.start || ""
          )
        );
      const dvName = appData.dailyDv?.[dateKey] || null;
      const isFmPresent = appData.dailyFmPresent?.[dateKey] || false;
      return {
        date,
        dow,
        dateKey,
        persons,
        acts,
        chars,
        dvName,
        isFmPresent,
        sk,
      };
    });

    // ── Printopmaak (A3 liggend, één pagina) ─────────────────────
    const esc = (x) => String(x ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");
    // CP-paren: [lichte tint, kleur, donker]
    const PAAR = {
      crea: ["#C4D4FF", "#9DB4F6", "#00192F"],
      entertainment: ["#FFE2A1", "#FFCA55", "#7A5226"],
      springkussen: ["#FFB0D6", "#FF699B", "#700606"],
      fm: ["#DAFFD1", "#045E51", "#045E51"],
      overig: ["#EEF2F6", "#CBD5E1", "#334155"],
    };
    const actPaar = (a) => {
      const t = (a.type || "").toLowerCase();
      const ti = (a.title || "").toLowerCase();
      if (t === "crea") return PAAR.crea;
      if (t === "springkussen" || ti.includes("springkussen")) return PAAR.springkussen;
      return PAAR.entertainment;
    };
    const dienstPaar = (t) => PAAR[(t || "").toLowerCase()] || PAAR.overig;
    const korteTitel = (t) =>
      String(t || "")
        .replace(/orry\s*(&|and)\s*friends/gi, "O&F")
        .trim();

    const persoonH = (p) => {
      const firstName = p.name.trim().split(" ")[0];
      const staff =
        (appData.staffList || []).find((s) => s.name === p.name) ||
        (appData.staffList || []).find((s) => s.name.split(" ")[0] === firstName);
      const photo = staff?.profilePhoto || SEED_PROFILE_PHOTOS[p.name] || SEED_PROFILE_PHOTOS[firstName];
      const [licht, , donker] = dienstPaar(p.shifts[0]?.type);
      const foto = photo
        ? `<img class=fo src="${esc(photo)}" alt="">`
        : `<div class="fo fi" style="background:${licht};color:${donker}">${esc(p.name.trim().charAt(0).toUpperCase())}</div>`;
      const kar = p.char
        ? `<span class=kar style="background:${chCol(p.char)};color:${chCol(p.char, "text")}">${esc(p.char.charAt(0).toUpperCase() + p.char.slice(1))}</span>`
        : "";
      const diensten = p.shifts
        .map((s) => {
          const naam = (s.name || s.title || s.dienstName || s.label || "").trim();
          return `<div class=di><span class=tijd>${esc(s.start || "")}–${esc(s.end || "")}</span>${naam ? `<span class=dn>${esc(naam)}</span>` : ""}</div>`;
        })
        .join("");
      return `<div class=pr style="border-left-color:${dienstPaar(p.shifts[0]?.type)[1]}">${foto}<div class=pi><div class=nm>${esc(p.name)} ${kar}</div>${diensten}</div></div>`;
    };

    const actH = (a) => {
      const [licht, , donker] = actPaar(a);
      const [st, en] = String(a.time || "").split(/\s*-\s*/);
      return `<div class=ac style="background:${licht};color:${donker}"><span class=at>${esc(st || "")}${en ? `<small>–${esc(en)}</small>` : ""}</span><span class=an>${esc(korteTitel(a.title))}</span></div>`;
    };

    const dayH = (day) => {
      const sk = day.sk;
      return `<section class=day>
  <div class=kop><div class=nr>${day.date.getDate()}</div><div><div class=wd>${DF[day.dow]}</div><div class=mn>${day.date.toLocaleDateString("nl-NL", { month: "long" })}</div></div></div>
  <div class=info>
    ${day.dvName ? `<div class=ir><span class=il>Dagverantwoordelijke</span><span class=iv>${esc(day.dvName)}</span></div>` : ""}
    ${day.isFmPresent ? `<div class=ir><span class=il>FM</span><span class=iv>Maik</span></div>` : ""}
    <div class="ir sk${sk.open ? "" : " dicht"}"><span class=il>Springkussen</span><span class=iv>${sk.open ? `${esc(sk.start)}–${esc(sk.end)}` : "dicht"}</span>${sk.open && sk.staff.length ? `<span class=iw>${esc(sk.staff.join(", "))}</span>` : ""}</div>
  </div>
  <div class=body>
    <div class=blok><div class=bt>Team</div>${day.persons.length ? day.persons.map(persoonH).join("") : `<div class=leeg>Niemand ingepland</div>`}</div>
    <div class=blok><div class=bt>Programma</div>${day.acts.length ? day.acts.map(actH).join("") : `<div class=leeg>Geen activiteiten</div>`}</div>
  </div>
</section>`;
    };

    const legenda = [
      ["Crea", PAAR.crea],
      ["Entertainment & O&F", PAAR.entertainment],
      ["Springkussen", PAAR.springkussen],
      ["FM", PAAR.fm],
    ]
      .map(([l, [, k]]) => `<span class=lg><i style="background:${k}"></i>${esc(l)}</span>`)
      .join("");
    const karLegenda = ["Orry", "Bing", "Woops", "Rep"]
      .map((n) => `<span class=lg><i class=rond style="background:${chCol(n)}"></i>${n}</span>`)
      .join("");

    const html = `<!DOCTYPE html><html lang="nl"><head><meta charset=utf-8><title>${esc(weekLabel)}</title><style>
@font-face{font-family:Bagoss;src:url(${basis}/fonts/bagos-regular.woff2) format("woff2"),url(${basis}/fonts/bagos-regular.ttf) format("truetype");font-weight:400}
@font-face{font-family:Bagoss;src:url(${basis}/fonts/bagos-medium.woff2) format("woff2"),url(${basis}/fonts/bagos-medium.ttf) format("truetype");font-weight:500}
@page{size:A3 landscape;margin:0}
*{box-sizing:border-box;margin:0;padding:0;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}
html{-webkit-text-size-adjust:100%;text-size-adjust:100%}
html,body{width:420mm;height:297mm;overflow:hidden;background:#fff}
body{font-family:Bagoss,Arial,sans-serif;font-weight:400;letter-spacing:0;color:#00192F;--k:1}
.pg{width:420mm;height:297mm;padding:9mm 9mm 6mm;display:flex;flex-direction:column;gap:5mm}
.hd{display:flex;align-items:flex-end;justify-content:space-between;gap:8mm;flex-shrink:0}
.ht{font-size:24pt;font-weight:500;color:#045E51;line-height:1}
.hs{font-size:9pt;color:#045E51;margin-top:1.5mm}
.lgs{display:flex;flex-direction:column;align-items:flex-end;gap:1.5mm}
.lgr{display:flex;gap:4mm;align-items:center}
.lg{display:inline-flex;align-items:center;gap:1.5mm;font-size:8pt}
.lg i{width:3.2mm;height:3.2mm;border-radius:1mm;display:inline-block}
.lg i.rond{border-radius:50%}
.gr{display:grid;grid-template-columns:repeat(7,1fr);gap:3mm;flex:1;min-height:0}
.day{display:flex;flex-direction:column;border-radius:4mm;overflow:hidden;background:#F6F8F7;min-height:0}
.kop{display:flex;align-items:center;gap:2.5mm;background:#045E51;color:#fff;padding:2.5mm 3.5mm}
.nr{font-size:22pt;font-weight:500;line-height:1}
.wd{font-size:10.5pt;font-weight:500;line-height:1.1}
.mn{font-size:8pt;opacity:.8;line-height:1.1}
.info{padding:2mm 3mm;display:flex;flex-direction:column;gap:1mm;background:#DAFFD1}
.ir{display:flex;align-items:baseline;gap:1.5mm;flex-wrap:wrap;font-size:calc(8.5pt * var(--k))}
.il{font-size:calc(7pt * var(--k));text-transform:uppercase;letter-spacing:0;color:#045E51;opacity:.85}
.iv{font-weight:500;color:#045E51}
.ir.sk .il,.ir.sk .iv{color:#700606}
.ir.dicht .iv{color:#6B7280;font-weight:400}
.iw{font-size:calc(7.5pt * var(--k));color:#700606;width:100%}
.body{flex:1;min-height:0;overflow:hidden;padding:2.5mm;display:flex;flex-direction:column;gap:3mm}
.blok{display:flex;flex-direction:column;gap:calc(1.4mm * var(--k))}
.bt{font-size:calc(7pt * var(--k));text-transform:uppercase;color:#045E51;font-weight:500;margin-bottom:.5mm}
.leeg{font-size:calc(8pt * var(--k));color:#9CA3AF}
.pr{display:flex;align-items:center;gap:calc(2mm * var(--k));background:#fff;border-radius:2.5mm;padding:calc(1.4mm * var(--k)) 2mm;border-left:1.3mm solid #CBD5E1}
.fo{width:calc(9.5mm * var(--k));height:calc(9.5mm * var(--k));border-radius:50%;object-fit:cover;flex-shrink:0}
.fi{display:flex;align-items:center;justify-content:center;font-weight:500;font-size:calc(10pt * var(--k))}
.pi{min-width:0;flex:1}
.nm{font-size:calc(10pt * var(--k));font-weight:500;line-height:1.15}
.kar{font-size:calc(7pt * var(--k));font-weight:500;border-radius:999px;padding:.2mm 1.6mm;vertical-align:1px}
.di{font-size:calc(8pt * var(--k));line-height:1.3}
.tijd{font-variant-numeric:tabular-nums;font-weight:500;display:block}
.dn{display:block;opacity:.85}
.ac{display:flex;gap:2mm;align-items:baseline;border-radius:2mm;padding:calc(1.2mm * var(--k)) 2mm;font-size:calc(8.8pt * var(--k));line-height:1.2}
.at{font-variant-numeric:tabular-nums;font-weight:500;flex-shrink:0;min-width:calc(10mm * var(--k))}
.at small{font-size:.75em;font-weight:400;opacity:.8}
.an{min-width:0}
.ft{font-size:7pt;color:#9CA3AF;text-align:right;flex-shrink:0}
</style></head><body>
<div class=pg>
  <div class=hd>
    <div><div class=ht>${esc(weekLabel)}</div><div class=hs>Kids &amp; Entertainment · weekoverzicht</div></div>
    <div class=lgs><div class=lgr>${legenda}</div><div class=lgr>${karLegenda}</div></div>
  </div>
  <div class=gr>${days.map(dayH).join("")}</div>
  <div class=ft>Afgedrukt ${new Date().toLocaleDateString("nl-NL")}</div>
</div>
<script>
// Alles op één A3: tekst iets kleiner als een dag te vol is (zelfde maat voor alle dagen)
(document.fonts ? document.fonts.ready : Promise.resolve()).then(function(){
  // begin groot (vult de pagina) en maak kleiner tot de volste dag past
  var k = 1.35, dagen = document.querySelectorAll('.body');
  document.body.style.setProperty('--k', k);
  var te = function(){ for (var i = 0; i < dagen.length; i++) if (dagen[i].scrollHeight > dagen[i].clientHeight + 1) return true; return false; };
  while (te() && k > 0.55) { k = Math.round((k - 0.04) * 100) / 100; document.body.style.setProperty('--k', k); }
  window.__klaar = true;
});
<\/script>
</body></html>`;
  return { html, weekLabel };
};
